import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import i18n from 'i18next';
import { X, FileText, FileSpreadsheet, Loader2, Search, CheckSquare, Square } from 'lucide-react';
import Button from '../ui/Button';
import api from '../../services/api';

interface Campaign {
  id: string;
  name: string;
  status?: string;
  platform?: string;
  platforms?: string[];
  total_spent?: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  campaigns: Campaign[];
}

const PRESETS = [
  { key: 'last7', label: 'Últimos 7 dias', days: 7 },
  { key: 'last30', label: 'Últimos 30 dias', days: 30 },
  { key: 'last90', label: 'Últimos 90 dias', days: 90 },
  { key: 'custom', label: 'Personalizado', days: 0 },
];

const toIsoDate = (d: Date) => d.toISOString().split('T')[0];

const ConsolidatedReportModal: React.FC<Props> = ({ open, onClose, campaigns }) => {
  const { t } = useTranslation();
  const [preset, setPreset] = useState('last30');
  const today = toIsoDate(new Date());
  const [startDate, setStartDate] = useState(toIsoDate(new Date(Date.now() - 30 * 86400000)));
  const [endDate, setEndDate] = useState(today);
  const [format, setFormat] = useState<'pdf' | 'xlsx'>('pdf');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(campaigns.map(c => c.id)));
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (open) setSelectedIds(new Set(campaigns.map(c => c.id)));
  }, [open, campaigns]);

  const applyPreset = (key: string) => {
    setPreset(key);
    const pr = PRESETS.find(p => p.key === key);
    if (!pr || pr.days === 0) return;
    setStartDate(toIsoDate(new Date(Date.now() - pr.days * 86400000)));
    setEndDate(today);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return campaigns;
    return campaigns.filter(c => c.name.toLowerCase().includes(q));
  }, [campaigns, search]);

  const toggleAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map(c => c.id)));
  };
  const toggle = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const [progress, setProgress] = useState(0);

  const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

  const generate = async () => {
    if (selectedIds.size === 0) {
      toast.error(t('campaigns.consolidatedReport.noCampaigns', 'Selecione pelo menos uma campanha'));
      return;
    }
    setLoading(true);
    setProgress(0);
    const toastId = toast.loading(
      format === 'pdf'
        ? t('campaigns.consolidatedReport.generatingPdf', 'Gerando PDF consolidado...')
        : t('campaigns.consolidatedReport.generatingXlsx', 'Gerando Excel consolidado...')
    );
    try {
      // 1) Enfileira o job
      const submit = await api.post('/campaigns/export-multi', {
        campaignIds: Array.from(selectedIds),
        format,
        startDate,
        endDate,
        language: (i18n.language || 'pt').slice(0, 2),
      });
      const jobId = submit.data?.data?.jobId;
      if (!jobId) throw new Error('No jobId returned');

      // 2) Polling — até 5min max
      const deadline = Date.now() + 5 * 60 * 1000;
      while (Date.now() < deadline) {
        await sleep(2000);
        const s = await api.get(`/campaigns/export-jobs/${jobId}/status`);
        const st = s.data?.data;
        if (!st) continue;
        setProgress(st.progress || 0);
        toast.loading(
          `${format === 'pdf' ? 'PDF' : 'Excel'} · ${st.progress || 0}%`,
          { id: toastId }
        );
        if (st.status === 'completed') break;
        if (st.status === 'failed') throw new Error(st.error || 'Job failed');
      }

      // 3) Download do arquivo pronto
      const dl = await api.get(`/campaigns/export-jobs/${jobId}/download`, { responseType: 'blob' });
      const blob = new Blob([dl.data], {
        type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = dl.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `relatorio_consolidado.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(t('campaigns.consolidatedReport.success', 'Relatório gerado com sucesso!'), { id: toastId });
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Erro';
      toast.error(t('campaigns.consolidatedReport.error', 'Erro: {{msg}}', { msg }), { id: toastId, duration: 5000 });
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              {t('campaigns.consolidatedReport.title', 'Relatório Consolidado')}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {t('campaigns.consolidatedReport.subtitle', 'Selecione campanhas e período — gera PDF ou Excel pronto pra cliente')}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body scroll */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {/* Period */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              {t('campaigns.consolidatedReport.period', 'Período')}
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {PRESETS.map(p => (
                <button
                  key={p.key}
                  onClick={() => applyPreset(p.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    preset === p.key
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {t(`campaigns.consolidatedReport.${p.key}`, p.label)}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400">
                  {t('common.startDate', 'Data inicial')}
                </label>
                <input
                  type="date"
                  value={startDate}
                  max={endDate}
                  onChange={(e) => { setStartDate(e.target.value); setPreset('custom'); }}
                  className="w-full mt-0.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400">
                  {t('common.endDate', 'Data final')}
                </label>
                <input
                  type="date"
                  value={endDate}
                  min={startDate}
                  max={today}
                  onChange={(e) => { setEndDate(e.target.value); setPreset('custom'); }}
                  className="w-full mt-0.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* Format */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              {t('campaigns.consolidatedReport.format', 'Formato')}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setFormat('pdf')}
                className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-colors ${
                  format === 'pdf'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="w-10 h-10 rounded-lg bg-red-50 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div className="text-left">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">PDF</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {t('campaigns.consolidatedReport.pdfHint', 'Relatório visual pra cliente')}
                  </div>
                </div>
              </button>
              <button
                onClick={() => setFormat('xlsx')}
                className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-colors ${
                  format === 'xlsx'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="w-10 h-10 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                  <FileSpreadsheet className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="text-left">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">Excel</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {t('campaigns.consolidatedReport.xlsxHint', '7 abas pra análise')}
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Campaigns */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t('campaigns.consolidatedReport.campaigns', 'Campanhas')}
                <span className="ml-2 text-xs font-normal text-gray-500">
                  ({selectedIds.size} / {filtered.length})
                </span>
              </label>
              <button
                onClick={toggleAll}
                className="text-xs text-primary-500 hover:text-primary-600 font-medium"
              >
                {selectedIds.size === filtered.length
                  ? t('common.unselectAll', 'Desmarcar tudo')
                  : t('common.selectAll', 'Selecionar tudo')}
              </button>
            </div>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('campaigns.consolidatedReport.searchPlaceholder', 'Buscar...')}
                className="w-full pl-9 pr-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
            <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-800">
              {filtered.length === 0 ? (
                <div className="text-center text-xs text-gray-500 py-8">
                  {t('campaigns.consolidatedReport.noneFound', 'Nenhuma campanha encontrada')}
                </div>
              ) : filtered.map(c => {
                const checked = selectedIds.has(c.id);
                const platLabel = (p: string) => p === 'openai' ? 'ChatGPT' : p;
                const plats = Array.isArray(c.platforms) && c.platforms.length > 0
                  ? c.platforms.map(platLabel).join(' · ')
                  : (platLabel(c.platform || '') || '—');
                return (
                  <button
                    key={c.id}
                    onClick={() => toggle(c.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${
                      checked ? 'bg-primary-50/50 dark:bg-primary-900/10' : ''
                    }`}
                  >
                    {checked
                      ? <CheckSquare className="w-4 h-4 text-primary-500 flex-shrink-0" />
                      : <Square className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{c.name}</div>
                      <div className="text-xs text-gray-500 capitalize">{plats} · {c.status || 'draft'}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 rounded-b-2xl">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            onClick={generate}
            disabled={loading || selectedIds.size === 0}
            leftIcon={loading ? <Loader2 className="w-4 h-4 animate-spin" /> : undefined}
          >
            {loading
              ? progress > 0
                ? `${t('campaigns.consolidatedReport.generating', 'Gerando...')} ${progress}%`
                : t('campaigns.consolidatedReport.generating', 'Gerando...')
              : t('campaigns.consolidatedReport.generate', 'Gerar relatório ({{n}})', { n: selectedIds.size })}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConsolidatedReportModal;
